import type {PreparedText, SourcePoint, SourceSelection} from '@github-ui/taillights-worker/document'

export type NativeSelectionMap = Pick<PreparedText, 'lines' | 'lineNumbers' | 'lineStarts' | 'sourceToDisplay' | 'text'>

/** Maps a UTF-16 offset in displayed text to its original source position, including folded projections. */
export function getSourcePoint(map: NativeSelectionMap, offset: number): SourcePoint | null {
  if (!Number.isInteger(offset) || offset < 0 || offset > map.text.length) return null
  let lower = 0
  let upper = map.lineStarts.length - 1
  while (lower <= upper) {
    const middle = Math.floor((lower + upper) / 2)
    const start = map.lineStarts[middle]
    const sourceLine = map.lineNumbers[middle]
    if (start === undefined || sourceLine === undefined) return null
    const nextStart = map.lineStarts[middle + 1] ?? Number.POSITIVE_INFINITY
    if (offset < start) {
      upper = middle - 1
    } else if (offset >= nextStart) {
      lower = middle + 1
    } else {
      return {line: sourceLine, column: offset - start}
    }
  }
  return null
}

/** Converts native selection endpoints to source coordinates while preserving forward or backward direction. */
export function getSourceSelection(
  map: NativeSelectionMap,
  startOffset: number,
  endOffset: number,
  direction: SourceSelection['direction'],
): SourceSelection | null {
  const start = getSourcePoint(map, startOffset)
  const end = getSourcePoint(map, endOffset)
  return start && end && startOffset <= endOffset ? {start, end, direction} : null
}

function getNativeOffset(map: NativeSelectionMap, point: SourcePoint) {
  const displayLine = map.sourceToDisplay.get(point.line)
  if (displayLine === undefined) return null
  const lineStart = map.lineStarts[displayLine]
  const line = map.lines[displayLine]
  if (
    lineStart === undefined ||
    line === undefined ||
    !Number.isInteger(point.column) ||
    point.column < 0 ||
    point.column > line.length
  ) {
    return null
  }
  return lineStart + point.column
}

/** Maps a source selection to displayed offsets, returning null when either endpoint is hidden or invalid. */
export function getNativeSelection(map: NativeSelectionMap, selection: SourceSelection) {
  const start = getNativeOffset(map, selection.start)
  const end = getNativeOffset(map, selection.end)
  if (start === null || end === null || start > end) return null
  return {start, end, direction: selection.direction}
}

/** Maps one DOM selection contained within a native block to source coordinates, including styled text nodes. */
export function getBlockSelection(source: HTMLElement | null, map: NativeSelectionMap): SourceSelection | null {
  const selection = source?.ownerDocument.getSelection()
  if (!source || !selection || selection.rangeCount !== 1) return null
  const range = selection.getRangeAt(0)
  if (!source.contains(range.startContainer) || !source.contains(range.endContainer)) return null

  const flatOffset = (node: Node, offset: number) => {
    const prefix = source.ownerDocument.createRange()
    prefix.selectNodeContents(source)
    prefix.setEnd(node, offset)
    return prefix.toString().length
  }
  const start = flatOffset(range.startContainer, range.startOffset)
  const end = flatOffset(range.endContainer, range.endOffset)
  const direction = selection.isCollapsed
    ? 'none'
    : selection.anchorNode === range.startContainer && selection.anchorOffset === range.startOffset
      ? 'forward'
      : 'backward'
  return getSourceSelection(map, start, end, direction)
}
